import { useCallback, useState } from "react";

interface UsePersistProps<T> {
  stateName: string;
  initialValue: T;
  persistable?: boolean;
}

export const usePersistableState = <T>({
  stateName,
  initialValue,
  persistable = false,
}: UsePersistProps<T>): [T, (value: T) => void, clearState: () => void] => {
  const name = `persist/${stateName}`;

  const clearState = () => {
    localStorage.removeItem(name);
  };

  const getFromStorage = <V>(itemName: string, defaultValue?: V) => {
    if (!persistable) return defaultValue;
    try {
      const val = JSON.parse(localStorage.getItem(itemName) + "");
      if (val !== null) {
        return val;
      } else {
        localStorage.setItem(itemName, JSON.stringify(defaultValue));
      }
    } catch {
      return defaultValue;
    }
  };

  const [state, setState] = useState<T>(getFromStorage<T>(name, initialValue));

  const setValue = useCallback(
    (value: T) => {
      if (!persistable) return value;
      localStorage.setItem(name, JSON.stringify(value));
      setState(value);
    },
    [name],
  );

  return [state, setValue, clearState];
};
